'use client';

import { useAppParameters } from '@app/app/_context/AppParametersContext/useAppParameters';
import { AccessibilityMenu } from '@app/app/_widgets/AccessabilityMenu/AccessibilityMenu';
import { AllInsurancesMenu } from '@app/app/_widgets/AllInsurancesMenu/AllInsurancesMenu';
import { LanguageSelector } from '@app/app/_widgets/LanguageSelector/LanguageSelector';
import { ProfileMenu } from '@app/app/_widgets/ProfileMenu/ProfileMenu';
import { Client } from '@app/domain/model/Client';
import { Profile } from '@app/domain/model/Profile';
import { MenuIcon } from '@app/app/_icons/MenuIcon';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { MouseEvent, useCallback } from 'react';
import { twJoin, twMerge } from 'tailwind-merge';
import { useHomeNavigation } from '@app/app/_hooks/useHomeNavigation';
import { useNavigationGuard } from '@app/app/_context/NavigationGuardContext/useNavigationGuard';
import { useTranslations } from 'next-intl';
import { FontSize } from '@app/domain/model/FontSize';
import { useHighContrastMode } from '@app/app/_context/AccessibilityContext/useHighContrastMode';
import { useFontSize } from '@app/app/_context/AccessibilityContext/useFontSize';
import { GhostButton } from '@app/app/_components/GhostButton/GhostButton';

interface Props {
    authenticated?: boolean;
    profile?: Profile | null;
    clients?: Client[];
    availableLocales?: string[];
    selectedClientId?: string;
    hideMobileMenu?: boolean;
    hideMobileLanguageSelector?: boolean;
    hideMobileAccessibilityOptions?: boolean;
    hideMobileLogos?: boolean;
    headerTitle?: Nullable<string>;
}

export function Header({
    authenticated,
    profile,
    clients = [],
    selectedClientId,
    hideMobileMenu = false,
    hideMobileLanguageSelector = false,
    hideMobileAccessibilityOptions = false,
    hideMobileLogos = false,
}: Props) {
    const t = useTranslations();
    const router = useRouter();
    const { publicFolderPrefix, tenant } = useAppParameters();
    const { shouldGuard } = useNavigationGuard();
    const {
        homeLink,
        handleHomeClick,
        isEsalesTransport,
        isEsalesTravel,
        isEsalesLife,
        isEsalesPersonalAccidents,
        isEsalesHomeInsurance,
    } = useHomeNavigation();
    const isHighContrastModeEnabled = useHighContrastMode();
    const fontSize = useFontSize();

    const isNonLifeEsales = isEsalesTransport || isEsalesTravel || isEsalesPersonalAccidents || isEsalesHomeInsurance;

    const handleMenuToggle = useCallback(() => {
        dispatchEvent(new Event('app.menu-toggled'));
    }, []);

    const handleLogoClick = useCallback(
        (e: MouseEvent<HTMLAnchorElement>) => {
            if (shouldGuard) {
                e.preventDefault();
                if (confirm(t('general.areYouSureYouWantToLeave'))) {
                    router.push(homeLink);
                }
                return;
            }
            handleHomeClick();
        },
        [shouldGuard, t, router, homeLink, handleHomeClick],
    );

    return (
        <header className="z-20 border-b border-neutral-200 bg-white px-4 py-3 md:px-6 md:py-4">
            <div className="flex items-center justify-between">
                <div className="flex flex-row items-center gap-4 xs:gap-8">
                    {!hideMobileLogos && (
                        <div className="flex items-center self-stretch xs:mr-6">
                            <div
                                className={twMerge(
                                    'flex items-center gap-6',
                                    fontSize !== FontSize.Normal && 'max-md:gap-0',
                                )}>
                                <Link href={homeLink} onClick={handleLogoClick} className="flex self-center">
                                    <Image
                                        className={twMerge('max-h-8', fontSize !== FontSize.Normal && 'max-md:hidden')}
                                        src={
                                            isHighContrastModeEnabled
                                                ? `${publicFolderPrefix}/compensa_logo_white.svg`
                                                : `${publicFolderPrefix}/compensa-logo.svg`
                                        }
                                        alt={t('altTexts.compensaLogo')}
                                        width={86}
                                        height={24}
                                        priority
                                    />
                                </Link>

                                {isNonLifeEsales ? (
                                    <AllInsurancesMenu />
                                ) : (
                                    !isEsalesLife && tenant == 'EE' && (
                                        <Link href={homeLink} onClick={handleLogoClick} className="flex">
                                            <Image
                                                className="max-h-8 w-auto"
                                                src={
                                                    isHighContrastModeEnabled
                                                        ? `${publicFolderPrefix}/seesam-logo-white.png`
                                                        : `${publicFolderPrefix}/seesam-logo.svg`
                                                }
                                                alt={t('altTexts.seesamLogo')}
                                                width={87}
                                                height={41}
                                                priority
                                                unoptimized
                                            />
                                        </Link>
                                    )
                                )}
                            </div>
                        </div>
                    )}
                </div>

                <div className={twJoin('flex items-center gap-4 xs:gap-6')}>
                    {authenticated && (
                        <ProfileMenu profile={profile} clients={clients} selectedClientId={selectedClientId} />
                    )}

                    <AccessibilityMenu
                        className={twJoin(authenticated && hideMobileAccessibilityOptions ? 'hidden md:flex' : 'flex')}
                    />

                    <div className={twJoin(authenticated && hideMobileLanguageSelector && 'hidden md:flex')}>
                        <LanguageSelector />
                    </div>

                    {authenticated && !hideMobileMenu && (
                        <GhostButton
                            className="p-1 md:hidden"
                            onClick={handleMenuToggle}
                            aria-label={t('ariaLabels.menuToggle')}>
                            <MenuIcon className="size-6" />
                        </GhostButton>
                    )}
                </div>
            </div>
        </header>
    );
}
